import { useState, useEffect, useRef, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { callClaude, validateMeta } from "../utils/claude";
import { makeCSV, makeTSV, dlFile } from "../utils/export";
import Badge from "../components/Badge";

// Нормалізація URL для матчингу зі знімком позицій (узгоджено з server/services/positionsGate.js).
const normUrl = (u) => {
  if (!u) return "";
  let s = String(u).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "");
  s = s.split("?")[0].split("#")[0].replace(/\/+$/, "");
  return s;
};

export default function GenView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const [cls, setCls] = useState(store.clusters || []);
  const [run, setRun] = useState(false);
  const [prog, setProg] = useState({ d: 0, t: 0, ok: 0, er: 0 });
  const [logs, setLogs] = useState([]);
  const [showLog, setShowLog] = useState(true);
  const [bs, setBs] = useState(2);
  const [lim, setLim] = useState(5);
  const [flt, setFlt] = useState("all");
  const [urlSearch, setUrlSearch] = useState("");
  const [page, setPage] = useState(0);
  const [eId, setEId] = useState(null);
  const [eT, setET] = useState("");
  const [eD, setED] = useState("");
  const abRef = useRef(false);
  const lgRef = useRef();
  const perPage = 25;
  const [minusWords, setMinusWords] = useState("");
  const [minusApplying, setMinusApplying] = useState(false);
  const [minusProg, setMinusProg] = useState({ d: 0, t: 0 });

  // Гейт по позиціях для масової генерації (avg≥поріг → лише поштучно через ↻).
  const [avgByPage, setAvgByPage] = useState(null); // { hasSnapshot, threshold, byUrl }
  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    apiFetch(`/api/projects/${activeId}/positions/avg-by-page`).then(r => r.json()).then(d => {
      if (alive && d && !d.error) setAvgByPage(d);
    }).catch(() => {});
    return () => { alive = false; };
  }, [activeId]);
  const gateThreshold = avgByPage?.threshold || 10;
  // Сторінка в ТОП (avg ≤ поріг) — мета працює, масово НЕ чіпаємо (лише поштучно).
  const isProtected = (url) => {
    if (!avgByPage?.hasSnapshot) return false; // нема знімку → гейт неактивний для генерації
    const a = avgByPage.byUrl?.[normUrl(url)]?.avgPos;
    return a != null && a <= gateThreshold;
  };
  const gatedCount = useMemo(() => {
    if (!avgByPage?.hasSnapshot) return 0;
    return cls.filter(c => !c.meta && isProtected(c.url)).length;
  }, [cls, avgByPage]); // eslint-disable-line

  // Tabs: "generate" (existing) | "augment" (new — extends existing meta only)
  const [genTab, setGenTab] = useState("generate");
  const [augStats, setAugStats] = useState([]);    // [{id, url, hfKeyword, generated, parsed}]
  const [augLoading, setAugLoading] = useState(false);
  const [augTask, setAugTask] = useState(null);    // {taskId, total, done, status, message}
  const [augSkipTop10, setAugSkipTop10] = useState(true);
  const [augSearch, setAugSearch] = useState("");

  // Load current-stats on tab switch
  useEffect(() => {
    if (genTab !== "augment" || !activeId) return;
    let alive = true;
    apiFetch(`/api/projects/${activeId}/meta-gen/current-stats`).then(r => r.json()).then(d => {
      if (alive) setAugStats(Array.isArray(d) ? d : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [genTab, activeId]);

  const reloadAugStats = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-gen/current-stats`);
      const d = await r.json();
      setAugStats(Array.isArray(d) ? d : []);
    } catch {}
  };

  const runParseCurrent = async () => {
    if (augLoading) return;
    setAugLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-gen/parse-current`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { flash("Помилка: " + (d.error || "")); setAugLoading(false); return; }
      setAugTask({ taskId: d.taskId, total: d.total, done: 0, message: "Парсимо…" });
      // Poll task status
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${d.taskId}`);
          if (!sR.ok) return;
          const s = await sR.json();
          setAugTask({ taskId: d.taskId, total: s.progress?.total || d.total, done: s.progress?.done || 0, message: s.status });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setAugLoading(false);
            await reloadAugStats();
            flash(s.status === "completed" ? "Парсинг завершено" : "Помилка парсингу: " + (s.error || ""));
          }
        } catch {}
      }, 2000);
    } catch (e) {
      flash("Помилка: " + e.message);
      setAugLoading(false);
    }
  };

  const runAugment = async () => {
    if (augLoading) return;
    setAugLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-gen/augment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ includeTop10: !augSkipTop10 }),
      });
      const d = await r.json();
      if (!r.ok) { flash("Помилка: " + (d.error || "")); setAugLoading(false); return; }
      if (d.message) {
        flash(d.message);
        setAugLoading(false);
        return;
      }
      setAugTask({ taskId: d.taskId, total: d.total, done: 0, message: "Доповнюємо…", skippedTop10: d.skippedTop10 });
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${d.taskId}`);
          if (!sR.ok) return;
          const s = await sR.json();
          setAugTask(prev => ({ ...prev, total: s.progress?.total || d.total, done: s.progress?.done || 0, message: s.status }));
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setAugLoading(false);
            await reloadAugStats();
            flash(s.status === "completed" ? `Доповнено ${s.progress?.ok || 0} мета` : "Помилка");
          }
        } catch {}
      }, 2000);
    } catch (e) {
      flash("Помилка: " + e.message);
      setAugLoading(false);
    }
  };

  useEffect(() => { setStore(activeId, { ...getStore(activeId), rows: store.rows, clusters: cls }); }, [cls]);
  // Sync from store if clusters loaded after initial render
  useEffect(() => { if (store.clusters?.length && !cls.length) setCls(store.clusters); }, [store.clusters]);
  useEffect(() => { lgRef.current && (lgRef.current.scrollTop = lgRef.current.scrollHeight); }, [logs]);
  const saveClsToDb = (updated) => saveClusters(activeId, updated || cls);

  const log = (msg, type = "ok") => setLogs(l => [...l.slice(-300), { m: msg, t: type, ts: new Date().toLocaleTimeString() }]);

  const generate = async (overrideBs, overrideLim) => {
    if (run) return;
    const useBs = overrideBs || bs;
    const useLim = overrideLim !== undefined ? overrideLim : lim;
    abRef.current = false;
    setRun(true);
    setLogs([]);

    let ready = cls.filter(c => !c.meta);
    // Гейт: масово не генеруємо сторінки в ТОП (avg≤поріг) — їх мета працює, лише поштучно через ↻.
    let gatedOut = 0;
    if (avgByPage?.hasSnapshot) {
      const before = ready.length;
      ready = ready.filter(c => !isProtected(c.url));
      gatedOut = before - ready.length;
    }
    const todo = useLim > 0 ? ready.slice(0, useLim) : ready;
    if (!todo.length) {
      flash(gatedOut ? `Усі сторінки в ТОП-${gateThreshold} — масова генерація заборонена (мета працює). Генеруйте поштучно (↻).` : "Нет URL");
      setRun(false); return;
    }

    setProg({ d: 0, t: todo.length, ok: 0, er: 0 });
    log(`Старт: ${todo.length} URL, batch=${useBs}, Claude Sonnet${gatedOut ? ` · 🔴 пропущено ТОП-${gateThreshold}: ${gatedOut} (лише поштучно)` : ""}`);
    if (gatedOut) flash(`🔴 Пропущено ${gatedOut} стор. у ТОП-${gateThreshold} — генеруйте поштучно (↻)`);

    let okN = 0, erN = 0;

    for (let i = 0; i < todo.length; i += useBs) {
      if (abRef.current) { log("⛔ Остановлено", "warn"); break; }
      const batch = todo.slice(i, i + useBs);
      log(`📦 Batch ${Math.floor(i / bs) + 1}/${Math.ceil(todo.length / bs)}`);

      // Mark generating
      setCls(prev => prev.map(c => batch.find(b => b.id === c.id) ? { ...c, status: "GENERATING" } : c));

      const results = await Promise.allSettled(
        batch.map(async cl => {
          let retries = 3;
          while (retries > 0) {
            try {
              const meta = await callClaude(cl, proj.domain, proj.competitors, [], proj.version);
              const iss = validateMeta(meta, cl);
              setCls(prev => prev.map(c => c.id === cl.id ? { ...c, meta, status: iss.length ? "NEEDS_REVIEW" : "DONE", issues: iss, error: "" } : c));
              okN++;
              const short = cl.url.replace(/https?:\/\/[^/]+/, "").slice(-40);
              log(`✓ ${short} → ${(meta.title || "").slice(0, 35)}...`);
              return;
            } catch (err) {
              retries--;
              if (retries > 0) {
                log(`🔄 Retry: ${err.message?.slice(0, 50)}`, "warn");
                await new Promise(r => setTimeout(r, 2000));
              } else {
                setCls(prev => prev.map(c => c.id === cl.id ? { ...c, status: "ERROR", error: err.message } : c));
                erN++;
                log(`✕ ${cl.url.replace(/https?:\/\/[^/]+/, "").slice(-35)}: ${err.message?.slice(0, 60)}`, "err");
              }
            }
          }
        })
      );

      setProg({ d: Math.min(i + bs, todo.length), t: todo.length, ok: okN, er: erN });
      // Save to DB after each batch to prevent data loss
      setCls(prev => { saveClsToDb(prev); return prev; });
      if (!abRef.current) await new Promise(r => setTimeout(r, 600));
    }

    // Wait for React to flush all setCls updates
    await new Promise(r => setTimeout(r, 800));
    setRun(false);
    log(`🏁 Итого: ✓${okN} ✕${erN}`);
    flash(`Готово: ${okN}✓ ${erN}✕`);
    // Save to DB
    setCls(prev => { saveClsToDb(prev); return prev; });
  };

  // Background server-side generation
  const generateOnServer = async (filter = "empty") => {
    if (run) return;
    setRun(true);
    log("Запуск генерації на сервері (фонова задача)...");
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-gen/start`, {
        method: "POST",
        body: JSON.stringify({ filter, batchSize: bs }),
      });
      const d = await r.json();
      if (d.ok && d.taskId) {
        const skip = d.skippedHighPosition ? ` · 🔴 пропущено ТОП-${gateThreshold}: ${d.skippedHighPosition} (лише поштучно)` : "";
        log(`Задача створена: ${d.total} кластерів${skip}. Можна закрити сторінку — генерація продовжиться.`);
        flash("Генерація запущена на сервері. Прогрес у повідомленнях.");
      } else if (d.ok && d.message) {
        // Усі сторінки відсіяні гейтом або немає кластерів.
        log(d.message, "warn");
        flash(d.message);
      } else {
        log(d.error || d.message || "Error", "err");
      }
    } catch (e) { log("Помилка: " + e.message, "err"); }
    setRun(false);
  };

  const regenOne = async cl => {
    if (run) return;
    setCls(prev => prev.map(c => c.id === cl.id ? { ...c, status: "GENERATING", meta: null } : c));
    try {
      const meta = await callClaude(cl, proj.domain, proj.competitors, [], proj.version);
      const iss = validateMeta(meta, cl);
      setCls(prev => {
        const updated = prev.map(c => c.id === cl.id ? { ...c, meta, status: iss.length ? "NEEDS_REVIEW" : "DONE", issues: iss, error: "" } : c);
        saveClsToDb(updated);
        return updated;
      });
    } catch (err) {
      setCls(prev => prev.map(c => c.id === cl.id ? { ...c, status: "ERROR", error: err.message } : c));
    }
  };

  const [eH, setEH] = useState("");
  const startEdit = c => { setEId(c.id); setET(c.meta?.title || ""); setEH(c.meta?.h1 || ""); setED(c.meta?.description || ""); };
  const saveEdit = c => {
    setCls(prev => {
      const updated = prev.map(x => x.id === c.id ? { ...x, meta: { ...x.meta, title: eT, h1: eH, description: eD }, status: "DONE", issues: [] } : x);
      saveClsToDb(updated);
      return updated;
    });
    setEId(null); flash("Сохранено");
  };

  // ─── Minus words helpers ───
  const parsedMinus = useMemo(() =>
    minusWords.split(/[,\n]+/).map(w => w.trim().toLowerCase()).filter(Boolean),
    [minusWords]
  );

  const affectedClusters = useMemo(() => {
    if (!parsedMinus.length) return [];
    return cls.filter(c => {
      if (!c.meta) return false;
      const hay = [c.meta.title, c.meta.h1, c.meta.description].filter(Boolean).join(" ").toLowerCase();
      return parsedMinus.some(w => hay.includes(w));
    });
  }, [cls, parsedMinus]);

  const applyMinusWords = async () => {
    if (minusApplying || run || !affectedClusters.length || !parsedMinus.length) return;
    setMinusApplying(true);
    setMinusProg({ d: 0, t: affectedClusters.length });
    log(`🚫 Мінус-слова: ${parsedMinus.join(", ")} → ${affectedClusters.length} кластерів`);

    let done = 0;
    for (let i = 0; i < affectedClusters.length; i += 2) {
      const batch = affectedClusters.slice(i, i + 2);
      setCls(prev => prev.map(c => batch.find(b => b.id === c.id) ? { ...c, status: "GENERATING" } : c));

      await Promise.allSettled(
        batch.map(async cl => {
          try {
            const meta = await callClaude(cl, proj.domain, proj.competitors, parsedMinus, proj.version);
            const iss = validateMeta(meta, cl);
            setCls(prev => prev.map(c => c.id === cl.id ? { ...c, meta, status: iss.length ? "NEEDS_REVIEW" : "DONE", issues: iss, error: "" } : c));
            const short = cl.url.replace(/https?:\/\/[^/]+/, "").slice(-40);
            log(`✓ (мінус) ${short}`);
          } catch (err) {
            setCls(prev => prev.map(c => c.id === cl.id ? { ...c, status: "ERROR", error: err.message } : c));
            log(`✕ (мінус) ${cl.url.replace(/https?:\/\/[^/]+/, "").slice(-35)}: ${err.message?.slice(0, 60)}`, "err");
          }
        })
      );

      done += batch.length;
      setMinusProg({ d: done, t: affectedClusters.length });
      if (i + 2 < affectedClusters.length) await new Promise(r => setTimeout(r, 600));
    }

    await new Promise(r => setTimeout(r, 800));
    setMinusApplying(false);
    log(`🏁 Мінус-слова: перегенеровано ${affectedClusters.length} кластерів`);
    flash(`Мінус-слова: ${affectedClusters.length} перегенеровано`);
    setCls(prev => { saveClsToDb(prev); return prev; });
  };

  const cn = { all: cls.length, done: 0, ready: 0, error: 0, review: 0, few: 0 };
  cls.forEach(c => {
    if (c.meta) cn.done++;
    else if (c.status === "NOT_ENOUGH_KEYWORDS") { cn.few++; cn.ready++; }
    else if (c.status === "ERROR") { cn.error++; cn.ready++; } // помилки можна перезапустити
    else cn.ready++; // READY, DONE without meta, NEEDS_REVIEW without meta — all available for generation
  });

  const filteredByStatus = flt === "all" ? cls : cls.filter(c => {
    if (flt === "done") return c.status === "DONE";
    if (flt === "ready") return c.status === "READY" && !c.meta;
    if (flt === "error") return c.status === "ERROR";
    if (flt === "review") return c.status === "NEEDS_REVIEW";
    return true;
  });
  const filtered = urlSearch.trim()
    ? filteredByStatus.filter(c => c.url && c.url.toLowerCase().includes(urlSearch.toLowerCase().trim()))
    : filteredByStatus;

  const totalPg = Math.ceil(filtered.length / perPage);
  const pgData = filtered.slice(page * perPage, (page + 1) * perPage);
  const pct = prog.t ? Math.round(prog.d / prog.t * 100) : 0;

  // ── Augment tab: filter + summary ──
  const augFiltered = augStats.filter(r => !augSearch || (r.url || "").toLowerCase().includes(augSearch.toLowerCase()));
  const augWithParsed = augStats.filter(r => r.parsed);
  const augTop10Count = augWithParsed.filter(r => r.parsed?.gscPosition != null && r.parsed.gscPosition <= 10).length;
  const augShortTitle = augWithParsed.filter(r => (r.parsed?.lenTitle || 0) < 90).length;
  const augShortDesc = augWithParsed.filter(r => (r.parsed?.lenDesc || 0) < 130).length;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>🏷️ Генерация Meta</h2>
      <p style={{ ...muted, marginBottom: 14 }}>{proj.name}</p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, borderBottom: `1px solid ${t.border}` }}>
        <button
          onClick={() => setGenTab("generate")}
          style={{
            padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: genTab === "generate" ? "#6366f1" : t.textMuted,
            borderBottom: genTab === "generate" ? "2px solid #6366f1" : "2px solid transparent",
            marginBottom: -1, fontFamily: "inherit",
          }}
        >🔄 Генерувати з нуля</button>
        <button
          onClick={() => setGenTab("augment")}
          style={{
            padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: genTab === "augment" ? "#6366f1" : t.textMuted,
            borderBottom: genTab === "augment" ? "2px solid #6366f1" : "2px solid transparent",
            marginBottom: -1, fontFamily: "inherit",
          }}
          title="Спарсити поточні Title/Description зі сторінок і доповнити їх (без перезапису)"
        >✨ Доповнити мета</button>
      </div>

      {genTab === "augment" && (
        <div>
          <div style={{ ...card, marginBottom: 14, padding: "12px 16px", borderLeft: "4px solid #22c55e", fontSize: 13, lineHeight: 1.6 }}>
            <strong>Доповнення мета</strong> — парсимо поточні Title/Description зі сторінок сайту, дивимось середню позицію в GSC, і ДОПОВНЮЄМО (а не замінюємо) ті мета, які можна покращити.
            <br/><span style={{ color: t.textMuted, fontSize: 12 }}>За замовчуванням сторінки в TOP-10 пропускаються — вони вже добре ранжуються, ризиковано чіпати.</span>
          </div>

          {/* Controls */}
          <div style={{ ...card, marginBottom: 14, padding: "12px 16px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={runParseCurrent}
              disabled={augLoading}
              style={{ ...btnP, opacity: augLoading ? 0.5 : 1 }}
              title="Завантажити HTML кожної сторінки і витягти поточні Title/H1/Description + GSC-позицію"
            >🔄 Завантажити поточні мета</button>

            <button
              onClick={runAugment}
              disabled={augLoading || !augWithParsed.length}
              style={{ ...btnP, background: "linear-gradient(135deg, #059669, #10b981)", opacity: (augLoading || !augWithParsed.length) ? 0.5 : 1 }}
              title="Дополнити existing мета через AI"
            >✨ Дополнити мета</button>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", marginLeft: 8 }}>
              <input type="checkbox" checked={augSkipTop10} onChange={e => setAugSkipTop10(e.target.checked)} />
              Не змінювати сторінки в TOP-10 ({augTop10Count})
            </label>

            {augTask && (
              <span style={{ fontSize: 12, color: t.textMuted, marginLeft: "auto" }}>
                {augTask.message === "completed" ? "✓ Завершено" : `⏳ ${augTask.done}/${augTask.total}`}
                {augTask.skippedTop10 ? ` · пропущено TOP-10: ${augTask.skippedTop10}` : ""}
              </span>
            )}
          </div>

          {/* Summary */}
          {augWithParsed.length > 0 && (
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                ["Спарсено", augWithParsed.length, "#10b981"],
                ["TOP-10 (skip)", augTop10Count, "#f59e0b"],
                ["Title <90", augShortTitle, "#ef4444"],
                ["Desc <130", augShortDesc, "#ef4444"],
              ].map(([l, n, c], i) => (
                <div key={i} style={{ background: dark ? "#252535" : "#f9fafb", borderRadius: 10, padding: "8px 14px", textAlign: "center", minWidth: 90 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{n}</div>
                  <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>{l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          {augStats.length > 0 && (
            <input
              type="text" value={augSearch} onChange={e => setAugSearch(e.target.value)}
              placeholder="🔍 Пошук по URL"
              style={{ marginBottom: 10, border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 12, background: t.bgCard, color: t.text, fontFamily: "inherit", minWidth: 320 }}
            />
          )}

          {/* Table */}
          {augStats.length === 0 ? (
            <div style={{ ...card, padding: 30, textAlign: "center", color: t.textMuted }}>
              Немає кластерів. Натисни «🔄 Завантажити поточні мета» щоб почати.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr>
                  <th style={th}>URL</th>
                  <th style={{ ...th, width: 280 }}>Поточний Title</th>
                  <th style={{ ...th, width: 60 }}>L</th>
                  <th style={{ ...th, width: 280 }}>Поточний Description</th>
                  <th style={{ ...th, width: 60 }}>L</th>
                  <th style={{ ...th, width: 80 }}>GSC поз.</th>
                  <th style={{ ...th, width: 100 }}>Статус</th>
                </tr></thead>
                <tbody>
                  {augFiltered.map(r => {
                    const p = r.parsed;
                    const pos = p?.gscPosition;
                    const inTop10 = pos != null && pos <= 10;
                    const tooShort = p && ((p.lenTitle || 0) < 90 || (p.lenDesc || 0) < 130);
                    const status = !p ? { l: "не спарсено", c: t.textMuted }
                      : inTop10 ? { l: "🚫 не міняти (TOP-10)", c: "#f59e0b" }
                      : tooShort ? { l: "⚠ можна доповнити", c: "#22c55e" }
                      : { l: "✓ ок", c: "#10b981" };
                    return (
                      <tr key={r.id} style={{ borderBottom: `1px solid ${t.border}`, background: inTop10 ? (dark ? "rgba(245,158,11,.05)" : "rgba(245,158,11,.04)") : "transparent" }}>
                        <td style={{ ...td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.url}>
                          {(() => { try { return new URL(r.url).pathname; } catch { return r.url; } })()}
                        </td>
                        <td style={{ ...td, fontSize: 11 }} title={p?.title || ""}>{p?.title ? (p.title.length > 70 ? p.title.slice(0, 70) + "…" : p.title) : "—"}</td>
                        <td style={{ ...td, color: (p?.lenTitle || 0) < 90 ? "#ef4444" : "#10b981", fontWeight: 600 }}>{p?.lenTitle ?? "—"}</td>
                        <td style={{ ...td, fontSize: 11 }} title={p?.description || ""}>{p?.description ? (p.description.length > 80 ? p.description.slice(0, 80) + "…" : p.description) : "—"}</td>
                        <td style={{ ...td, color: (p?.lenDesc || 0) < 130 ? "#ef4444" : "#10b981", fontWeight: 600 }}>{p?.lenDesc ?? "—"}</td>
                        <td style={{ ...td, fontWeight: 600, color: inTop10 ? "#10b981" : pos != null && pos <= 30 ? "#f59e0b" : t.textMuted }}>
                          {pos != null ? pos.toFixed(1) : "—"}
                        </td>
                        <td style={{ ...td, fontSize: 11, color: status.c }}>{status.l}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {genTab === "generate" && (
      <div>
      {/* Stats */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {[["Всего", cn.all, dark ? t.text : "#333"], ["✓", cn.done, "#16a34a"], ["Ожидает", cn.ready, "#2563eb"], ["Мало", cn.few, "#ea580c"], ["⚠", cn.review, "#ca8a04"], ["✕", cn.error, "#dc2626"]].map(([l, n, c], i) => (
          <div key={i} style={{ background: dark ? "#252535" : "#f9fafb", borderRadius: 10, padding: "8px 14px", textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{n}</div>
            <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Гейт по позиціях */}
      {gatedCount > 0 && (
        <div style={{ ...card, marginBottom: 14, padding: "10px 14px", borderLeft: "4px solid #dc2626", fontSize: 12.5, lineHeight: 1.6, color: t.text }}>
          🔴 <strong>Гейт по позиціях:</strong> {gatedCount} стор. у ТОП-{gateThreshold} (avg ≤ {gateThreshold}) буде <strong>пропущено</strong> при масовій генерації — їх мета вже працює. Перегенеровуйте лише поштучно (кнопка ↻ у рядку), бо зміна може сильно просісти.
        </div>
      )}

      {/* Controls */}
      <div style={{ ...card, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button style={{ ...btnP, opacity: run ? 0.6 : 1 }} onClick={() => generate()} disabled={run || cn.ready === 0}>
          {run ? `⏳ ${prog.d}/${prog.t} (${pct}%)` : "▶ Генерировать"}
        </button>
        <button style={{ ...btnP, background: "linear-gradient(135deg, #059669, #10b981)", opacity: run ? 0.6 : 1 }} onClick={() => generate(5, 0)} disabled={run || cn.ready === 0}>
          Сгенерировать все ({cn.ready})
        </button>
        <button style={{ ...btnG, background: dark ? "#1a1a24" : "#f0f0ff", color: "#6366f1", border: "1px solid #6366f133" }} onClick={() => generateOnServer("empty")} disabled={run} title="Генерація на сервері — можна закрити сторінку">
          На сервері ({cn.ready})
        </button>
        {run && <button style={{ ...btnG, color: "#dc2626" }} onClick={() => abRef.current = true}>⏹ Стоп</button>}
        {!run && cn.done > 0 && <button style={{ ...btnSm, color: "#dc2626", borderColor: "#dc262633" }} onClick={() => {
          if (!window.confirm(`Видалити мета-теги з усіх ${cn.done} сторінок?`)) return;
          const updated = cls.map(c => ({ ...c, meta: null }));
          setCls(updated);
          saveClsToDb(updated);
          flash(`Мета-теги видалено з ${cn.done} сторінок`);
        }}>Видалити всі мета</button>}
        <span style={muted}>Batch:</span>
        <select value={bs} onChange={e => setBs(+e.target.value)} disabled={run} style={{ padding: "4px 6px", fontSize: 12, borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text }}>
          {[1, 2, 3, 5].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <span style={muted}>Лимит:</span>
        <select value={lim} onChange={e => setLim(+e.target.value)} disabled={run} style={{ padding: "4px 6px", fontSize: 12, borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text }}>
          <option value={0}>Все ({cn.ready})</option>
          {[5, 10, 25, 50, 100, 200].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {cn.done > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button style={btnSm} onClick={() => { dlFile(makeCSV(cls, proj.name), `${proj.name}_meta.csv`); flash("CSV ⬇"); }}>⬇ CSV</button>
            <button style={btnSm} onClick={() => { navigator.clipboard?.writeText(makeTSV(cls, proj.name)); flash("TSV 📋"); }}>📋 TSV</button>
          </div>
        )}
      </div>

      {/* Minus Words */}
      {cls.some(c => c.meta) && (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>🚫 Мінус-слова (через кому або по рядку)</label>
              <textarea
                value={minusWords}
                onChange={e => setMinusWords(e.target.value)}
                rows={3}
                placeholder="напр: недорого, дешево, низкие цены"
                disabled={minusApplying || run}
                style={{ ...inp, width: "100%", fontSize: 12, resize: "vertical" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 20 }}>
              <span style={{ fontSize: 12, color: affectedClusters.length > 0 ? "#dc2626" : t.textMuted, fontWeight: 600 }}>
                Знайдено в {affectedClusters.length} кластерах
              </span>
              <button
                style={{ ...btnP, background: "linear-gradient(135deg, #dc2626, #ef4444)", fontSize: 12, opacity: (!affectedClusters.length || run || minusApplying) ? 0.5 : 1 }}
                disabled={!affectedClusters.length || run || minusApplying}
                onClick={applyMinusWords}
              >
                {minusApplying ? `⏳ Перегенерація ${minusProg.d}/${minusProg.t}...` : "Застосувати мінус-слова"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      {(run || prog.t > 0) && (
        <div style={{ background: dark ? "#252535" : "#e5e7eb", borderRadius: 8, height: 24, marginBottom: 14, overflow: "hidden", position: "relative" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #6366f1, #a855f7)", borderRadius: 8, transition: "width .3s" }} />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>
            {prog.d}/{prog.t} — {pct}% ✓{prog.ok} ✕{prog.er}
          </div>
        </div>
      )}

      {/* Log */}
      {logs.length > 0 && <>
        <button style={{ ...btnSm, marginBottom: 8 }} onClick={() => setShowLog(!showLog)}>{showLog ? "▼" : "▶"} Лог ({logs.length})</button>
        {showLog && (
          <div ref={lgRef} style={{ maxHeight: 160, overflowY: "auto", background: "#1e1b2e", color: "#a6e3a1", borderRadius: 8, padding: 10, fontFamily: "monospace", fontSize: 11, marginBottom: 14, lineHeight: 1.5 }}>
            {logs.map((l, i) => (
              <div key={i} style={{ color: l.t === "err" ? "#f87171" : l.t === "warn" ? "#fbbf24" : "#86efac" }}>
                <span style={{ opacity: 0.4 }}>{l.ts}</span> {l.m}
              </div>
            ))}
          </div>
        )}
      </>}

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        {[["all", "Все", cn.all], ["done", "✓ Готово", cn.done], ["ready", "Ожидает", cn.ready], ["review", "⚠", cn.review], ["error", "✕", cn.error]].map(([k, l, n]) => (
          <button key={k} style={{ ...(flt === k ? btnP : btnSm), fontSize: 11 }} onClick={() => { setFlt(k); setPage(0); }}>{l} ({n})</button>
        ))}
        <input
          type="text" value={urlSearch} onChange={e => { setUrlSearch(e.target.value); setPage(0); }}
          placeholder="🔍 Пошук по URL"
          style={{ marginLeft: 6, border: `1px solid ${t.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 11, background: t.bgCard, color: t.text, fontFamily: "inherit", minWidth: 240 }}
        />
        {urlSearch && <span style={{ fontSize: 11, color: t.textMuted }}>{filtered.length} знайдено</span>}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>
            <th style={th}>URL</th><th style={th}>Title</th><th style={th}>H1</th><th style={th}>Description</th>
            <th style={{ ...th, width: 130 }}>Ключи в Title</th><th style={{ ...th, width: 70 }}>Ст.</th><th style={{ ...th, width: 50 }}></th>
          </tr></thead>
          <tbody>{pgData.map(c => {
            const isEdit = eId === c.id;
            return (
              <tr key={c.id} style={{ background: isEdit ? "#f5f3ff" : c.status === "GENERATING" ? "#eff6ff" : "" }}>
                <td style={td}>
                  <a href={c.url} target="_blank" rel="noopener" style={{ fontSize: 10, color: "#6366f1", wordBreak: "break-all", textDecoration: "underline" }}>{c.url.replace(/https?:\/\/[^/]+/, "")}</a><br />
                  <span style={{ fontSize: 11, color: "#6366f1" }}>{c.hfKeyword}</span>
                </td>
                <td style={td}>
                  {isEdit ? <textarea value={eT} onChange={e => setET(e.target.value)} rows={2} style={{ ...inp, fontSize: 12 }} /> :
                    c.meta?.title ? <><span style={{ fontSize: 12 }}>{c.meta.title}</span><br /><span style={muted}>{c.meta.title.length}с</span></> : <span style={muted}>—</span>}
                </td>
                <td style={td}>
                  {isEdit ? <textarea value={eH} onChange={e => setEH(e.target.value)} rows={2} style={{ ...inp, fontSize: 12 }} /> :
                    c.meta?.h1 ? <><span style={{ fontSize: 12, fontWeight: 600 }}>{c.meta.h1}</span><br /><span style={muted}>{c.meta.h1.length}с</span></> : <span style={muted}>—</span>}
                </td>
                <td style={td}>
                  {isEdit ? <textarea value={eD} onChange={e => setED(e.target.value)} rows={3} style={{ ...inp, fontSize: 12 }} /> :
                    c.meta?.description ? <><span style={{ fontSize: 12 }}>{c.meta.description}</span><br /><span style={muted}>{c.meta.description.length}с</span></> : <span style={muted}>—</span>}
                </td>
                <td style={td}>
                  {c.meta?.title && c.meta?.used_keywords ? (() => {
                    const titleLow = c.meta.title.toLowerCase();
                    // Only show keywords from used_keywords that actually appear in title
                    const inTitle = c.meta.used_keywords.filter(kw => {
                      const kwLow = kw.toLowerCase();
                      // Direct match
                      if (titleLow.includes(kwLow)) return true;
                      // Check if all significant words of the keyword are in title
                      const words = kwLow.split(/\s+/).filter(w => w.length > 2);
                      if (words.length === 0) return false;
                      const matched = words.filter(w => titleLow.includes(w));
                      return matched.length === words.length;
                    });
                    return inTitle.length ? inTitle.map((k, i) => (
                      <span key={i} style={{ display: "inline-block", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 5, padding: "1px 6px", margin: 1, fontSize: 10, color: "#166534" }}>{k}</span>
                    )) : <span style={muted}>—</span>;
                  })() : <span style={muted}>—</span>}
                </td>
                <td style={td}>
                  <Badge st={c.meta ? (c.issues?.length ? "NEEDS_REVIEW" : "DONE") : c.status} />
                  {c.error && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>{c.error.slice(0, 40)}</div>}
                </td>
                <td style={td}>
                  {isEdit ? <>
                    <button style={{ ...btnSm, color: "#16a34a" }} onClick={() => saveEdit(c)}>✓</button>
                    <button style={btnSm} onClick={() => setEId(null)}>✕</button>
                  </> : <>
                    {c.meta && <button style={btnSm} onClick={() => startEdit(c)}>✎</button>}
                    <button style={btnSm} onClick={() => regenOne(c)} disabled={run}>↻</button>
                  </>}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>

      {totalPg > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
          <button style={btnSm} disabled={page === 0} onClick={() => setPage(page - 1)}>← Назад</button>
          <span style={{ fontSize: 12, lineHeight: "28px" }}>{page + 1}/{totalPg}</span>
          <button style={btnSm} disabled={page >= totalPg - 1} onClick={() => setPage(page + 1)}>Далее →</button>
        </div>
      )}
      {filtered.length === 0 && <p style={{ textAlign: "center", color: t.textMuted, padding: 20 }}>Нет записей</p>}
      </div>
      )}
    </div>
  );
}
